import { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Checkbox,
    Alert,
} from "@mui/material";
import axiosInstance from "../../services/axios-instance";
import { DELETE_USER, EDIT_USER } from "../../services/endpoints";
import { AllUsersInterface } from "../../interface/allUsersInterface";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { z } from "zod";
import { ROLES } from "../../assets/data/roles";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { styled } from "@mui/material/styles";
import ResetPassword from "../elements/ResetPassword";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.text.primary,
    position: "relative",
}));

const StyledTableRow = styled(TableRow)(() => ({
    backgroundColor: "white",
}));

const roles = Object.keys(ROLES)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
        value: key,
        key: ROLES[key as keyof typeof ROLES],
    }));

const validationSchema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    email: z
        .string()
        .email({ message: "Invalid email address" })
        .min(1, { message: "Email is required" }),
});

interface TableUsersParams {
    users: AllUsersInterface[];
    setUsers: (users: AllUsersInterface[]) => void;
    setRefetchUsers: (refetch: boolean) => void;
}

const TableClientsDetail = ({ users, setUsers }: TableUsersParams) => {
    const [removeClient, setRemoveClient] = useState<any[]>([]);
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
    const [deleteUserName, setDeleteUserName] = useState<string | null>(null);
    const [resetUserId, setResetUserId] = useState<number | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openRemoveClient, setOpenRemoveClient] = useState(false);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const handleEditClick = (userId: number) => {
        setEditingUserId(userId);
    };

    const handleSaveClick = async (values: AllUsersInterface) => {
        const { id, name, email, role_id, level, additional_roles, birthday, public_logs } = values;
        const initialUser = users.find((user) => user.id === id);

        // Compare the name, email, and role_id with the initial values
        const editedName = name !== initialUser?.name ? name : undefined;
        const editedEmail = email !== initialUser?.email ? email : undefined;
        const editedBirthday = birthday !== initialUser?.birthday ? birthday : undefined;
        const editedRoleId =
            role_id !== initialUser?.role_id ? role_id : undefined;
        const editedLevel = level !== initialUser?.level ? level : undefined;
        const publicLogs = public_logs !== initialUser?.public_logs ? public_logs : undefined;
        const editedAdditionalRoles =
            JSON.stringify(additional_roles) !==
            JSON.stringify(initialUser?.additional_roles)
                ? additional_roles
                : undefined;

        const requestBody: any = {};
        if (editedName) requestBody.name = editedName;
        if (editedEmail) requestBody.email = editedEmail;
        if (editedBirthday) requestBody.birthday = editedBirthday;
        if (editedRoleId) requestBody.role_id = editedRoleId;
        if (editedLevel) requestBody.level = editedLevel;
        requestBody.public_logs = publicLogs;
        if (editedAdditionalRoles)
            requestBody.additional_roles = editedAdditionalRoles;

        const response = await axiosInstance.post(EDIT_USER(id), requestBody);

        if (response.status !== 200) {
            console.error("Error updating user");
            return;
        }

        // Update the user with the edited values
        const updatedUsers = users.map((user) => {
            if (user.id === values.id) {
                return values;
            }
            return user;
        });
        // Save the updated users array
        setUsers(updatedUsers);
        // show alert for success
        setAlertMessage("User updated successfully");
        setOpenAlert(true);
        // Clear the editing state
        setEditingUserId(null);
    };

    const handleDeleteClick = (userId: number, userName: string) => {
        setDeleteUserId(userId);
        setDeleteUserName(userName);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (deleteUserId !== null) {
            const response = await axiosInstance.delete(
                DELETE_USER(deleteUserId)
            );

            if (response.status !== 200) {
                console.error("Error deleting user");
                return;
            }
            const updatedUsers = users.filter(
                (user) => user.id !== deleteUserId
            );

            setUsers(updatedUsers);

            setAlertMessage("User deleted successfully");
            setOpenAlert(true);
        }
        // Clear the delete state
        setDeleteUserId(null);
        setDeleteUserName(null);
        setOpenDeleteDialog(false);
    };

    const handleRemoveClientConfirm = async () => {
            const response = await axiosInstance.delete(
                'users/'+removeClient[0]+'/clients/'+removeClient[1]
            );

            if (response.status !== 200) {
                console.error("Error deleting user");
                return;
            }
            const updatedUsers = users.map(user => {
                if (user.id === removeClient[0] && user.clients) {
                  return {
                    ...user,
                    clients: user.clients.filter(client => client.id !== removeClient[1])
                  };
                }
                return user;
              });

            setUsers(updatedUsers);

        setRemoveClient([]);
        setOpenRemoveClient(false);
    };

    const handleDeleteCancel = () => {
        // Clear the delete state
        setDeleteUserId(null);
        setDeleteUserName(null);
        setOpenDeleteDialog(false);
    };

    const handleRemoveClientCancel = () => {
        setOpenRemoveClient(false);
    };

    const handleResetClick = (userId: number) => {
        setResetUserId(userId);
        setOpenResetDialog(true);
    };

    const getTimelogs = (id: number | null) => {
        if(id) {
            window.location.href = "timelog/client/" + id;
        }
    };

    const handleResetCancel = () => {
        setResetUserId(null);
        setOpenResetDialog(false);
    };

    return (
        <>
            <TableContainer component={Paper}>
                <Formik
                    initialValues={
                        editingUserId
                            ? users.find(
                                  (user) => user.id === editingUserId
                              ) || {
                                  id: 0,
                                  name: "",
                                  email: "",
                                  role_id: 0,
                                  status: 0,
                                  level: "",
                                  additional_roles: [],
                                  birthday: "",
                                  profile_picture: null,
                                  profile_picture_focal_point: null,
                                  client_id: 0,
                                  public_logs: false,
                              }
                            : {
                                  id: 0,
                                  name: "",
                                  email: "",
                                  role_id: 0,
                                  status: 0,
                                  level: "",
                                  additional_roles: [],
                                  birthday: "",
                                  profile_picture: null,
                                  profile_picture_focal_point: null,
                                  client_id: 0,
                                  public_logs: false,
                              }
                    }
                    validationSchema={toFormikValidationSchema(
                        validationSchema
                    )}
                    onSubmit={handleSaveClick}
                    enableReinitialize
                >
                    {({ values }) => (
                        <Form id="formik-form">
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: "#f9fafc",
                                        }}
                                    >
                                        <StyledTableCell>No.</StyledTableCell>
                                        <StyledTableCell>DB ID</StyledTableCell>
                                        <StyledTableCell>Name</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>Role</StyledTableCell>
                                        <StyledTableCell>Public Logs</StyledTableCell>
                                        <StyledTableCell>
                                            Actions
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            Timelog
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user, index) => (
                                        <StyledTableRow key={user.id}>
                                            <StyledTableCell>
                                                {index+1}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {user.id}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        as={TextField}
                                                    />
                                                ) : (
                                                    user.name
                                                )}

                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="name"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        type="email"
                                                        name="email"
                                                        as={TextField}
                                                    />
                                                ) : (
                                                    user.email
                                                )}
                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="email"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        name="role_id"
                                                        as={Select}
                                                        value={values.role_id}
                                                    >
                                                        {roles.map((role) => (
                                                            <MenuItem
                                                                key={role.key}
                                                                value={role.key}
                                                            >
                                                                {role.value}
                                                            </MenuItem>
                                                        ))}
                                                    </Field>
                                                ) : (
                                                    roles.find(
                                                        (role) =>
                                                            role.key ===
                                                            user.role_id
                                                    )?.value
                                                )}
                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="role_id"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field name="public_logs">
                                                    {({ field }: any) => (
                                                    <Checkbox
                                                        checked={field.value}
                                                        onChange={(e) =>
                                                        field.onChange({
                                                            target: {
                                                            name: field.name,
                                                            value: e.target.checked,
                                                            },
                                                        })
                                                        }
                                                    />
                                                    )}
                                                </Field>
                                                ) : (
                                                    user.public_logs ? 
                                                    <div className="text-lg centered">✓</div>
                                                    : 
                                                    <div className="text-lg centered">✗</div>
                                                )}
                                                </StyledTableCell>
                                            <StyledTableCell>
                                                <div style={{ display: "inline-flex" }}>
                                                {editingUserId === user.id ? (
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        type="submit"
                                                        form="formik-form"
                                                    >
                                                        Save
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={() =>
                                                            handleEditClick(
                                                                user.id
                                                            )
                                                        }
                                                    >
                                                        <EditIcon></EditIcon>
                                                    </Button>
                                                )}
                                                <Button
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            user.id,
                                                            user.name
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon></DeleteIcon>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        handleResetClick(
                                                            user.id
                                                        )
                                                    }
                                                >
                                                    <LockResetIcon></LockResetIcon>
                                                    </Button>
                                                </div>
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Button
                                                    onClick={() =>
                                                        //@ts-ignore
                                                        getTimelogs(user.client_id)
                                                    }
                                                >
                                                    <ExitToAppOutlinedIcon />
                                                </Button>
                                            </StyledTableCell>
                                            <Dialog
                                                open={
                                                    openResetDialog &&
                                                    user.id == resetUserId
                                                }
                                            >
                                                <DialogTitle>
                                                    New Password
                                                </DialogTitle>
                                                <ResetPassword
                                                    onClose={handleResetCancel}
                                                    userId={user.id}
                                                    setAlertMessage={
                                                        setAlertMessage
                                                    }
                                                    setOpenAlert={setOpenAlert}
                                                />
                                            </Dialog>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Form>
                    )}
                </Formik>
                <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
                    <DialogTitle>Confirm Deactivate</DialogTitle>
                    <DialogContent>
                        Are you sure you want to deactivate {deleteUserName}?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="secondary">
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={openRemoveClient} onClose={handleRemoveClientCancel}>
                    <DialogTitle>Confirm Client Unassign</DialogTitle>
                    <DialogContent>
                        Are you sure you want to unassign this client?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleRemoveClientCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleRemoveClientConfirm} color="secondary">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </TableContainer>
            <Snackbar
                open={openAlert}
                autoHideDuration={2000}
                onClose={() => setOpenAlert(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    elevation={6}
                    variant="filled"
                    onClose={() => setOpenAlert(false)}
                    severity="success"
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default TableClientsDetail;
